import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  RichTextContent,
  EditableSlotGroup,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';

const petsImageDef: SlotGroupDefinition = {
  label: 'Pets Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Resident with their pet',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const petsCtaDef: SlotGroupDefinition = {
  label: 'Pets Learn More CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/pet-policy',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Learn More',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function PetsCallout() {
  const petsImage = useSlotGroup('petscallout.image', petsImageDef);
  const petsCta = useSlotGroup('petscallout.cta', petsCtaDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up pets-section">
      {/* Full-bleed background image */}
      <EditableSlotGroup groupPath="petscallout.image" definition={petsImageDef}>
        <VibesitesImage
          url={petsImage.image || ''}
          alt={petsImage.alt || 'Resident with their pet'}
          className="pets-bg-img"
          priority
        />
      </EditableSlotGroup>

      {/* Text overlay — right side */}
      <div className="pets-overlay">
        <div className="pets-content">
          <RichTextContent
            path="petscallout.title"
            label="Pets Title"
            defaultValue="<p>We Love Our Furry Residents!</p>"
            as="h2"
            className="pets-title"
          />

          <RichTextContent
            path="petscallout.blurb"
            label="Pets Blurb"
            defaultValue="<p>We know the importance of pets to their fur-parents, so we prioritize them in the living experience at our communities.</p><p>We are proud to welcome your furry family members home. Learn more about our pet policy, fees, and any breed or weight guidelines below.</p>"
            as="div"
            className="pets-blurb"
          />

          <EditableSlotGroup groupPath="petscallout.cta" definition={petsCtaDef}>
            <a
              href={petsCta.url || '/pet-policy'}
              target={petsCta._target || '_self'}
              className="pets-cta"
            >
              {petsCta.label || 'Learn More'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .pets-section {
          position: relative;
          min-height: 560px;
          overflow: hidden;
        }
        .pets-bg-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .pets-overlay {
          position: relative;
          display: flex;
          justify-content: flex-end;
          min-height: 560px;
          background: rgba(0, 0, 0, 0.35);
        }
        .pets-content {
          display: flex;
          flex-direction: column;
          justify-content: center;
          width: 50%;
          padding: 4rem clamp(2rem, 5vw, 5rem);
          color: #ffffff;
        }
        .pets-title {
          font-family: var(--vibe-font-heading);
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: 0.01em;
          text-transform: uppercase;
          margin: 0 0 1.5rem;
        }
        .pets-blurb {
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          line-height: 1.7;
        }
        .pets-blurb p {
          margin: 0 0 1rem;
        }
        .pets-blurb p:last-child {
          margin-bottom: 0;
        }
        .pets-cta {
          display: inline-block;
          align-self: flex-start;
          margin-top: 2.25rem;
          padding: 0.9rem 3rem;
          border: 2px solid rgba(255, 255, 255, 0.85);
          border-radius: 9999px;
          color: #ffffff;
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          text-decoration: none;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .pets-cta:hover {
          background: #ffffff;
          color: #33514f;
        }
        @media (max-width: 900px) {
          .pets-section {
            min-height: 0;
          }
          .pets-overlay {
            min-height: 0;
            flex-direction: column;
            background: rgba(0, 0, 0, 0.45);
          }
          .pets-content {
            width: 100%;
            padding: 3rem 1.5rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetsCallout',
  displayName: 'Pets Callout',
  tokens: {
    'petscallout.title': {
      label: 'Pets Title',
      type: 'richtext',
      defaultValue: '<p>We Love Our Furry Residents!</p>',
    },
    'petscallout.blurb': {
      label: 'Pets Blurb',
      type: 'richtext',
      defaultField: 'property_pets_blurb',
      defaultValue:
        '<p>We know the importance of pets to their fur-parents, so we prioritize them in the living experience at our communities.</p><p>We are proud to welcome your furry family members home. Learn more about our pet policy, fees, and any breed or weight guidelines below.</p>',
    },
  },
  slotGroups: {
    'petscallout.image': petsImageDef,
    'petscallout.cta': petsCtaDef,
  },
};

(PetsCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetsCallout;
